import { AfterViewInit, Component, HostBinding, HostListener, Input, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { EMPTY, Subscription, timer } from "rxjs";
import { catchError, concatMap, distinctUntilChanged, mergeMap, takeUntil } from "rxjs/operators";
import { environment } from "../../../../../../environments/environment";
import { ComponentBase } from "../../../../../core/components/abstractions/component-base";
import { NotificationEventsService } from "../../../../../core/services/notifications/notification-events.service";
import { NotificationsCountDTO, NotificationsService } from "../../../../../core/services/swagger-gen/fordesk";

@Component({
  selector: "m-notification",
  templateUrl: "./notification.component.html",
  styleUrls: ["./notification.component.scss"]
})
export class NotificationComponent extends ComponentBase implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding("class")
  // tslint:disable-next-line:max-line-length
  public classes: string = "m-nav__item m-topbar__notifications m-topbar__notifications--img m-dropdown m-dropdown--large m-dropdown--header-bg-fill m-dropdown--arrow m-dropdown--align-center 	m-dropdown--mobile-full-width";

  @HostBinding("attr.m-dropdown-toggle") public attrDropdownToggle: string = "click";
  @HostBinding("attr.m-dropdown-persistent") public attrDropdownPersisten: string = "true";

  @Input() public animateShake: any;

  public newNotification: boolean = false;
  public notificationCount: number = 0;

  private refreshRate: number = 60000; // Fallback to one minute if no config is found
  private animateShakeIntervalId: number;
  private resetShakeIntervalId: number;
  private notificationPollingSubscription: Subscription | null = null;
  private lastNotificationRequestAt: number | null = null;

  constructor(
    private notificationsService: NotificationsService,
    private notificationEventsService: NotificationEventsService,
    private router: Router) {
    super();
    // animate icon shake and dot blink
    this.animateShakeIntervalId = window.setInterval(() => {
      this.animateShake = this.newNotification ? "m-animate-shake" : "";
    }, 3000);
    this.resetShakeIntervalId = window.setInterval(() => { this.animateShake = ""; this.newNotification = false; }, 6000);
  }

  public ngOnInit(): void {
    this.fetchInitialNotificationCount();
  }

  public ngAfterViewInit(): void {
    const refreshRate: number = environment.notificationsRefreshRate;
    if (refreshRate && !isNaN(refreshRate)) {
      this.refreshRate = +refreshRate;
    } else {
      const defaultRefreshRate: number = 120000;
      this.refreshRate = defaultRefreshRate;
    }

    this.subscribeToArchiveEvent();
    this.startNotificationPolling();
  }

  public ngOnDestroy() {
    this.stopNotificationPolling();
    window.clearInterval(this.animateShakeIntervalId);
    window.clearInterval(this.resetShakeIntervalId);
    return super.ngOnDestroy();
  }

  @HostListener("document:visibilitychange")
  public onVisibilityChange(): void {
    if (this.isDocumentVisible()) {
      this.startNotificationPolling();
      return;
    }

    this.stopNotificationPolling();
  }

  private subscribeToArchiveEvent(): void {
    this.notificationEventsService
      .hasArchived()
      .pipe(
        takeUntil(this.unsubscribe),
        distinctUntilChanged(),
        concatMap(() => this.getNotificationsCount())
      ).subscribe((notificationsCount: NotificationsCountDTO) => {
        this.updateNotificationCount(notificationsCount);
      });
  }

  private fetchInitialNotificationCount(): void {
    this.getNotificationsCount()
      .pipe(
        distinctUntilChanged()
      )
      .subscribe((notificationsCount: NotificationsCountDTO) => {
        this.updateNotificationCount(notificationsCount);
      });
  }

  private startNotificationPolling(): void {
    if (!this.isDocumentVisible()) {
      return;
    }

    this.stopNotificationPolling();

    this.notificationPollingSubscription = timer(this.getNextNotificationPollingDelay(), this.refreshRate)
      .pipe(
        mergeMap(() => this.getNotificationsCount()),
        catchError(() => EMPTY),
        takeUntil(this.unsubscribe)
      )
      .subscribe((notificationsCount: NotificationsCountDTO) => {
        this.updateNotificationCount(notificationsCount);
      });
  }

  private stopNotificationPolling(): void {
    if (this.notificationPollingSubscription) {
      this.notificationPollingSubscription.unsubscribe();
      this.notificationPollingSubscription = null;
    }
  }

  private getNextNotificationPollingDelay(): number {
    if (this.lastNotificationRequestAt == null) {
      return this.refreshRate;
    }

    const elapsedTimeSinceLastRequest: number = Date.now() - this.lastNotificationRequestAt;
    return elapsedTimeSinceLastRequest >= this.refreshRate
      ? 0
      : this.refreshRate - elapsedTimeSinceLastRequest;
  }

  private getNotificationsCount() {
    return this.notificationsService
      .getNotificationsCount({ disableLoader: true })
      .pipe(
        catchError(() => EMPTY),
        takeUntil(this.unsubscribe)
      );
  }

  private updateNotificationCount(notificationsCount: NotificationsCountDTO): void {
    if (notificationsCount == null) {
      return;
    }

    this.lastNotificationRequestAt = Date.now();
    this.newNotification = notificationsCount.nrOfActive > this.notificationCount;
    this.notificationCount = notificationsCount.nrOfActive;
  }

  private isDocumentVisible(): boolean {
    return document.visibilityState === "visible";
  }

  public navigateToNotifications(): void {
    this.router.navigateByUrl("notifications/list");
  }
}
